import dayjs, { Dayjs } from 'dayjs';
import { toNumber } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityStatsWithDaily, AmountTotal } from 'services/api/dashboard';
import { useTranslate } from '@tolgee/react';
import data1svg from './assets/data1.svg';
import data2svg from './assets/data2.svg';
import data3svg from './assets/data3.svg';
import data4svg from './assets/data4.svg';
import data5svg from './assets/data5.svg';
import data6svg from './assets/data6.svg';
import data7svg from './assets/data7.svg';
import data8svg from './assets/data8.svg';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { formatNumber } from 'utils/numberFormat';
import { initEcharts, applyChartTheme, ECharts } from 'lib/echarts';

const splitNumber = 5;

export function getChartMaxValue(data: number[], splitNumber: number, floatable = false) {
  const max = Math.max(...data);

  if (!max || !data.length) {
    return splitNumber;
  }

  let interval = 1;
  let min = 1;
  if (floatable) {
    if (max <= 1) {
      interval = 0.2;
      min = 0.1;
    }
    while (max <= min) {
      interval = interval / 10;
      min = min / 10;
      if (min <= 0.0001) {
        break;
      }
    }
  }

  return Math.ceil(max / splitNumber) * interval * splitNumber;
}

const numRender = (num?: number) => (num || num === 0 ? formatNumber(num) : '-');

function useActivityContents({
  item,
  to,
  getCurrencyTotalDisplay,
}: {
  item?: ActivityStatsWithDaily;
  to?: Dayjs;
  getCurrencyTotalDisplay: (list: AmountTotal[]) => AmountTotal | undefined;
}) {
  const { t } = useTranslate();

  const [gmv, setGmv] = useState(false);

  const data = useMemo(() => {
    if (item && to) {
      const maxDay = dayjs().subtract(1, 'days');
      const end = to.isSameOrBefore(maxDay, 'days') ? to : maxDay;
      return item?.dailyData?.reduce?.<typeof item.dailyData>((res, cur, index) => {
        const next = item.dailyData[index + 1];
        let data = cur;
        res.push(data);
        while (
          next
            ? dayjs(next.dt).subtract(1, 'days').isAfter(dayjs(data.dt), 'days')
            : dayjs(data.dt).isBefore(end, 'days')
        ) {
          data = {
            dt: dayjs(data.dt).add(1, 'days').format('YYYY-MM-DD'),
            dispatchBuyerCount: 0,
            activityTradeCount: 0,
            activityTradeAmount: 0,
            activityTradeAmountList: [],
            botTradeCount: 0,
            botTradeAmount: 0,
            botTradeAmountList: [],
            botTradeBuyerRate: 0,
          };
          res.push(data);
        }
        return res;
      }, []);
    }
  }, [item, to]);

  const gmvTotalDisplay = useMemo(() => {
    const activityTradeAmount = getCurrencyTotalDisplay(item?.activityTradeAmountTotalList ?? []);
    const botTradeAmount = getCurrencyTotalDisplay(item?.botTradeAmountTotalList ?? []);

    return {
      activityTradeAmountTotal: activityTradeAmount
        ? `${activityTradeAmount.unit} ${numRender(toNumber(activityTradeAmount.amount))}`
        : '-',
      botTradeAmountTotal: botTradeAmount
        ? `${botTradeAmount.unit} ${numRender(toNumber(botTradeAmount.amount))}`
        : '-',
      percent:
        activityTradeAmount && botTradeAmount
          ? formatNumber(
              toNumber(botTradeAmount.amount) / toNumber(activityTradeAmount.amount),
              '0.00%',
            )
          : null,
    };
  }, [item, getCurrencyTotalDisplay]);

  const chartDomRef1 = useRef<HTMLDivElement>(null);
  const chartDomRef2 = useRef<HTMLDivElement>(null);

  const chartRef1 = useRef<ECharts>();
  const chartRef2 = useRef<ECharts>();

  useEffect(() => {
    if (!data || !chartDomRef1.current || !chartDomRef2.current) {
      return;
    }
    if (!chartRef1.current) {
      chartRef1.current = initEcharts(chartDomRef1.current);
    }
    if (!chartRef2.current) {
      chartRef2.current = initEcharts(chartDomRef2.current);
    }

    const data1 = data.map(({ dispatchBuyerCount }) => dispatchBuyerCount);
    const data2 = data.map(({ botTradeCount }) => botTradeCount);
    const data3 = data.map(({ botTradeBuyerRate }) => botTradeBuyerRate * 100);
    const data4 = data.map(({ activityTradeCount, activityTradeAmountList }) => {
      if (gmv) {
        const display = getCurrencyTotalDisplay(activityTradeAmountList);
        return display ? toNumber(display.amount) : 0;
      }
      return activityTradeCount;
    });

    const max1 = getChartMaxValue([...data1, ...data2], splitNumber);
    const max2 = getChartMaxValue(data4, splitNumber);

    const options1 = {
      legend: {
        data: [t('小乐触达用户数'), t('小乐转化订单数'), t('小乐触达用户转化率')],
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: data.map(({ dt }) => dt),
      },
      yAxis: [
        {
          type: 'value',
          name: '',
          nameTextStyle: {
            align: 'left',
          },
          splitNumber,
          max: max1,
          interval: max1 / splitNumber,
        },
        {
          type: 'value',
          name: '',
          splitNumber,
          max: 100,
          axisLabel: {
            formatter: (value: number) => `${value}%`,
          },
        },
      ],
      tooltip: {
        trigger: 'axis',
      },
      series: [
        {
          data: data1,
          type: 'bar',
          name: t('小乐触达用户数'),
          itemStyle: {
            color: '#5B8FF9',
            opacity: 0.85,
          },
          barMaxWidth: 30,
        },
        {
          data: data2,
          type: 'bar',
          name: t('小乐转化订单数'),
          itemStyle: {
            opacity: 0.85,
            color: '#5AD8A6',
          },
          barMaxWidth: 30,
        },
        {
          data: data3,
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          name: t('小乐触达用户转化率'),
          itemStyle: {
            color: '#f6903d',
          },
          lineStyle: {
            color: '#f6903d',
          },
          tooltip: {
            valueFormatter: (value: number) => formatNumber(Number(value / 100), '0.00%'),
          },
        },
      ],
    };

    const options2 = {
      legend: {
        data: [gmv ? t('活动转化GMV') : t('活动转化订单数')],
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: data.map(({ dt }) => dt),
      },
      yAxis: [
        {
          type: 'value',
          name: '',
          nameTextStyle: {
            align: 'left',
          },
          splitNumber,
          max: max2,
          interval: max2 / splitNumber,
        },
      ],
      tooltip: {
        trigger: 'axis',
      },
      series: [
        {
          data: data4,
          type: 'bar',
          name: gmv ? t('活动转化GMV') : t('活动转化订单数'),
          itemStyle: {
            color: gmv ? '#5AD8A6' : '#5B8FF9',
            opacity: 0.85,
          },
          barMaxWidth: 30,
        },
      ],
    };

    chartRef1.current.setOption(applyChartTheme(options1));
    chartRef2.current.setOption(applyChartTheme(options2));
  }, [gmv, data, getCurrencyTotalDisplay, t]);

  const statsLine1 = (
    <Line>
      <li className={cardGradient1}>
        <div>{t('活动已推广天数')}</div>
        <p>{numRender(data?.length)}</p>
        <img src={data1svg} alt="data" />
      </li>
      <li className={cardGradient2}>
        <div>{t('小乐触达用户数')}</div>
        <p>{numRender(item?.dispatchBuyerTotal)}</p>
        <img src={data2svg} alt="data" />
      </li>
      <li className={cardGradient3}>
        <div>{t('小乐触达后下单用户数')}</div>
        <p>{numRender(item?.botTradeBuyerTotal)}</p>
        <img src={data3svg} alt="data" />
      </li>
      <li className={cardGradient4}>
        <div>{t('小乐转化订单')}</div>
        <p>{numRender(item?.botTradeTotal)}</p>
        <img src={data4svg} alt="data" />
        {/* {item?.activityTradeTotal && item.botTradeTotal ? (
          <span>
            {t('带来了其中{{x}}的订单', {
              x: numeral(item.botTradeTotal / item.activityTradeTotal).format('0.00%'),
            })}
          </span>
        ) : null} */}
      </li>
      <li className={cardGradient5}>
        <div>{t('小乐触达用户转化率')}</div>
        <p>{formatNumber(Number(item?.botTradeBuyerRate), '0.00%')}</p>
        <img src={data5svg} alt="data" />
      </li>
      <li className={cardGradient6}>
        <div>{t('小乐转化GMV')}</div>
        <p>{gmvTotalDisplay.botTradeAmountTotal}</p>
        <img src={data6svg} alt="data" />
        {/* {gmvTotalDisplay.percent ? (
          <span>
            {t('带来了其中{{x}}的收入', {
              x: gmvTotalDisplay.percent,
            })}
          </span>
        ) : null} */}
      </li>
    </Line>
  );

  const chart1 = <div ref={chartDomRef1} />;

  const statsLine2 = (
    <Line>
      <li className={cardGradient4}>
        <div>{t('活动转化订单')}</div>
        <p>{numRender(item?.activityTradeTotal)}</p>
        <img src={data7svg} alt="data" />
      </li>
      <li className={cardGradient6}>
        <div>{t('活动转化GMV')}</div>
        <p>{gmvTotalDisplay.activityTradeAmountTotal}</p>
        <img src={data8svg} alt="data" />
      </li>
      <li />
      <li />
      <li />
      <li />
    </Line>
  );

  const gmvToggle = (
    <Toggle>
      <ul>
        <li className={gmv ? undefined : toggleActive} onClick={() => setGmv(false)}>
          {t('转化订单')}
        </li>
        <li className={gmv ? toggleActive : undefined} onClick={() => setGmv(true)}>
          {t('转化GMV')}
        </li>
      </ul>
    </Toggle>
  );

  const chart2 = <div ref={chartDomRef2} />;

  return {
    statsLine1,
    chart1,
    statsLine2,
    gmvToggle,
    chart2,
    gmv,
    t,
  };
}

const Line = styled.ul`
  display: flex;
  align-items: stretch;
  gap: 16px;
  margin: 0;
  padding: 24px;
  list-style: none;
  > li {
    flex: 1 0 0;
    position: relative;
    min-height: 102px;
    padding: 16px 20px;
    border-radius: 8px;
    overflow: hidden;
    > div {
      color: var(--fett-text-secondary);
    }
    > p {
      margin: 2px 0;
      font-size: 16px;
      font-weight: 500;
    }
    > span {
      display: inline-block;
      padding-right: 18px;
      font-size: 12px;
      white-space: pre-wrap;
      color: #00bf69;
    }
    > img {
      position: absolute;
      width: 56px;
      right: 0;
      bottom: 0;
      z-index: 0;
    }
  }
`;

const Toggle = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  > ul {
    display: flex;
    align-items: stretch;
    height: 32px;
    margin: 0;
    padding: 0;
    border-radius: 16px;
    background: var(--fett-bg-subtle);
    list-style: none;
    > li {
      line-height: 32px;
      padding: 0 16px;
      color: var(--fett-text-tertiary);
      border-radius: 16px;
      cursor: pointer;
    }
  }
`;

/** 统计卡浅色渐变，暗色下统一覆盖为深色面板渐变 */
const darkCardGradient = `
  [data-color-mode='dark'] & {
    background: linear-gradient(180deg, var(--fett-bg-elevated) 0%, var(--fett-bg-panel) 100%);
  }
`;
const cardGradient1 = css`
  background: linear-gradient(180deg, #e8effe 0%, #f8fafe 99%);
  ${darkCardGradient}
`;
const cardGradient2 = css`
  background: linear-gradient(180deg, #e8f8fe 0%, #f8fefb 99%);
  ${darkCardGradient}
`;
const cardGradient3 = css`
  background: linear-gradient(180deg, #ffe9ed 0%, #fff5fa 99%);
  ${darkCardGradient}
`;
const cardGradient4 = css`
  background: linear-gradient(180deg, #eae8fe 0%, #f9f8fe 97%);
  ${darkCardGradient}
`;
const cardGradient5 = css`
  background: linear-gradient(180deg, #fef4e8 0%, #fefef8 99%);
  ${darkCardGradient}
`;
const cardGradient6 = css`
  background: linear-gradient(180deg, #ecfee8 0%, #fefef8 99%);
  ${darkCardGradient}
`;

/** GMV 切换选中态，暗色下换为品牌蓝透明底 + 亮蓝文字 */
const toggleActive = css`
  color: #336fff;
  background: #f0f7ff;
  [data-color-mode='dark'] & {
    color: #8aadff;
    background: rgba(51, 102, 255, 0.16);
  }
`;

export default useActivityContents;
